import React, { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import { ArrowLeftIcon, ArrowPathIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import { getLocalApiUrl, authFetch } from '../../config/api'
import HelpWysiwygEditor, { emptyBlock } from '../../components/help/HelpWysiwygEditor'
import HelpBlockRenderer from '../../components/help/HelpBlockRenderer'

function decodeKey(raw) {
  try {
    return decodeURIComponent(raw || '')
  } catch {
    return raw || ''
  }
}

function SectionCard({
  section,
  sectionIndex,
  onUpdate,
  onRemove,
  onUploadImage,
  uploadBusy,
  canDelete,
}) {
  const isFooter = section.section_key === 'legal-footer'
  const blocks = section.blocks || []

  return (
    <article
      className={`rounded-lg border p-3 scroll-mt-6 transition-shadow ${
        isFooter ? 'border-slate-200 bg-slate-50/50' : 'border-slate-200 bg-white shadow-sm'
      }`}
    >
      {!isFooter && (
        <input
          type="text"
          value={section.title}
          onChange={(e) => onUpdate(sectionIndex, { title: e.target.value })}
          placeholder="Section title"
          className="w-full text-sm font-bold text-slate-900 bg-transparent border-0 border-b border-transparent focus:border-slate-200 focus:ring-0 outline-none mb-3 pb-1"
        />
      )}

      {isFooter ? (
        <HelpWysiwygEditor
          blocks={blocks.length ? blocks : [emptyBlock('legal_footer')]}
          onChange={(next) => onUpdate(sectionIndex, { blocks: next })}
          onUploadImage={onUploadImage}
          uploadBusy={uploadBusy}
          showLegalFooter
        />
      ) : (
        <HelpWysiwygEditor
          blocks={blocks}
          onChange={(next) => onUpdate(sectionIndex, { blocks: next })}
          onUploadImage={onUploadImage}
          uploadBusy={uploadBusy}
        />
      )}

      <details className="mt-4 pt-3 border-t border-slate-100">
        <summary className="text-[10px] font-medium text-slate-400 cursor-pointer hover:text-slate-600">
          Section settings
        </summary>
        <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
          <div>
            <label className="block text-[10px] text-slate-500 mb-0.5">Section key</label>
            <input
              type="text"
              value={section.section_key}
              onChange={(e) => onUpdate(sectionIndex, { section_key: e.target.value })}
              className="w-full text-xs font-mono border border-slate-200 rounded px-2 py-1"
            />
          </div>
          <div>
            <label className="block text-[10px] text-slate-500 mb-0.5">Context keys</label>
            <input
              type="text"
              value={(section.context_keys || []).join(', ')}
              onChange={(e) =>
                onUpdate(sectionIndex, {
                  context_keys: e.target.value.split(',').map((x) => x.trim()).filter(Boolean),
                })
              }
              className="w-full text-xs font-mono border border-slate-200 rounded px-2 py-1"
              placeholder="tab:evidence"
            />
          </div>
        </div>
        {canDelete && (
          <button
            type="button"
            onClick={() => onRemove(sectionIndex)}
            className="mt-2 inline-flex items-center gap-1 text-xs text-red-600 hover:underline"
          >
            <TrashIcon className="w-3.5 h-3.5" />
            Delete section
          </button>
        )}
      </details>
    </article>
  )
}

export default function HelpCmsEditor() {
  const { screenKey: rawKey } = useParams()
  const screenKey = decodeKey(rawKey)
  const [screen, setScreen] = useState(null)
  const [sections, setSections] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [publishing, setPublishing] = useState(false)
  const [uploadBusy, setUploadBusy] = useState(false)
  const [previewOpen, setPreviewOpen] = useState(false)

  const load = useCallback(async () => {
    if (!screenKey) return
    setLoading(true)
    try {
      const enc = encodeURIComponent(screenKey)
      const r = await authFetch(getLocalApiUrl(`/api/admin/help/screens/${enc}`))
      const j = await r.json()
      if (!j.success || !j.data) throw new Error(j.error || 'Not found')
      setScreen(j.data)
      setSections(
        (j.data.sections || []).map((s) => ({
          ...s,
          blocks: s.draft_blocks?.length ? s.draft_blocks : s.blocks || [],
        }))
      )
    } catch (e) {
      toast.error(e.message || 'Failed to load')
      setScreen(null)
      setSections([])
    } finally {
      setLoading(false)
    }
  }, [screenKey])

  useEffect(() => {
    load()
  }, [load])

  const updateSection = (index, patch) => {
    setSections((prev) => prev.map((s, i) => (i === index ? { ...s, ...patch } : s)))
  }

  const addSection = () => {
    setSections((prev) => [
      ...prev.filter((s) => s.section_key !== 'legal-footer'),
      {
        section_key: `section-${prev.length + 1}`,
        title: 'New section',
        sort_order: (prev.length + 1) * 10,
        context_keys: [],
        blocks: [{ type: 'paragraph', text: '' }],
      },
      ...prev.filter((s) => s.section_key === 'legal-footer'),
    ])
  }

  const removeSection = (index) => {
    if (!window.confirm('Remove this section?')) return
    setSections((prev) => prev.filter((_, i) => i !== index))
  }

  const bodySections = sections.filter((s) => s.section_key !== 'legal-footer')
  const footerSection = sections.find((s) => s.section_key === 'legal-footer')

  const saveDraft = async () => {
    if (!screen) return
    setSaving(true)
    try {
      const enc = encodeURIComponent(screenKey)
      const r = await authFetch(getLocalApiUrl(`/api/admin/help/screens/${enc}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          route_path: screen.route_path,
          module: screen.module,
          title: screen.title,
          subtitle: screen.subtitle,
          sections: sections.map((s, i) => ({
            id: s.id,
            section_key: s.section_key,
            title: s.title,
            sort_order: s.sort_order ?? (i + 1) * 10,
            context_keys: s.context_keys || [],
            blocks: s.blocks || [],
          })),
        }),
      })
      const j = await r.json()
      if (!j.success) throw new Error(j.error || 'Save failed')
      toast.success('Draft saved')
      await load()
    } catch (e) {
      toast.error(e.message || 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  const publish = async () => {
    if (!window.confirm('Publish this help screen to all tenants?')) return
    setPublishing(true)
    try {
      await saveDraft()
      const enc = encodeURIComponent(screenKey)
      const r = await authFetch(getLocalApiUrl(`/api/admin/help/screens/${enc}/publish`), { method: 'POST' })
      const j = await r.json()
      if (!j.success) throw new Error(j.error || 'Publish failed')
      toast.success('Published')
      await load()
    } catch (e) {
      toast.error(e.message || 'Publish failed')
    } finally {
      setPublishing(false)
    }
  }

  const uploadImage = async (file) => {
    setUploadBusy(true)
    try {
      const enc = encodeURIComponent(screenKey)
      const fd = new FormData()
      fd.append('file', file)
      const r = await authFetch(getLocalApiUrl(`/api/admin/help/screens/${enc}/assets`), {
        method: 'POST',
        body: fd,
      })
      const j = await r.json()
      if (!j.success) throw new Error(j.error || 'Upload failed')
      return j.data.asset_id
    } catch (e) {
      toast.error(e.message || 'Upload failed')
      return null
    } finally {
      setUploadBusy(false)
    }
  }

  if (loading) {
    return <p className="text-sm text-gray-500 p-6">Loading editor…</p>
  }

  if (!screen) {
    return (
      <div className="p-6 space-y-3">
        <Link to="/admin/help" className="text-sm text-psg-teal hover:underline inline-flex items-center gap-1">
          <ArrowLeftIcon className="w-4 h-4" /> Back to list
        </Link>
        <p className="text-red-600">Screen not found.</p>
      </div>
    )
  }

  const previewSections = bodySections.map((s) => ({
    id: s.section_key,
    title: s.title,
    blocks: s.blocks,
  }))

  return (
    <div className="max-w-4xl mx-auto pb-16">
      {/* Toolbar */}
      <div className="sticky top-0 z-20 -mx-4 px-4 py-3 mb-4 bg-gray-50/95 backdrop-blur border-b border-gray-200 flex flex-wrap items-center justify-between gap-3">
        <Link to="/admin/help" className="text-sm text-psg-teal hover:underline inline-flex items-center gap-1">
          <ArrowLeftIcon className="w-4 h-4" /> Help CMS
        </Link>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setPreviewOpen((v) => !v)}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg bg-white hover:bg-gray-50"
          >
            {previewOpen ? 'Edit' : 'Preview'}
          </button>
          <button type="button" onClick={load} className="p-2 border rounded-lg bg-white hover:bg-gray-50">
            <ArrowPathIcon className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={saveDraft}
            disabled={saving || publishing}
            className="px-4 py-1.5 text-sm font-semibold border border-gray-300 rounded-lg bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save draft'}
          </button>
          <button
            type="button"
            onClick={publish}
            disabled={saving || publishing}
            className="px-4 py-1.5 text-sm font-semibold bg-psg-teal text-white rounded-lg hover:bg-psg-teal-600 disabled:opacity-50"
          >
            {publishing ? 'Publishing…' : 'Publish'}
          </button>
        </div>
      </div>

      {/* Document canvas */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-6 sm:px-10 py-8 min-h-[24rem]">
        <p className="text-[10px] font-mono text-slate-400 mb-1">{screen.screen_key}</p>
        <input
          type="text"
          value={screen.title}
          onChange={(e) => setScreen((s) => ({ ...s, title: e.target.value }))}
          placeholder="Help panel title"
          className="w-full text-2xl font-bold text-slate-900 bg-transparent border-0 focus:ring-0 outline-none placeholder:text-slate-300"
        />
        <input
          type="text"
          value={screen.subtitle || ''}
          onChange={(e) => setScreen((s) => ({ ...s, subtitle: e.target.value }))}
          placeholder="Subtitle (optional)"
          className="w-full mt-1 text-sm text-slate-500 bg-transparent border-0 focus:ring-0 outline-none placeholder:text-slate-300"
        />
        <p className="text-xs text-slate-400 mt-2">
          Status: <strong className="text-slate-600">{screen.status}</strong>
          {screen.published_at ? ` · Published ${new Date(screen.published_at).toLocaleString()}` : ''}
        </p>

        <div className="mt-6 space-y-3">
          {previewOpen ? (
            <>
              <p className="text-xs text-slate-500 px-1">
                Preview matches the tenant help panel. Press <strong>Esc</strong> styling is applied in-app.
              </p>
              {previewSections.map((s) => (
                <section key={s.id} className="rounded-lg border border-slate-200 p-4 bg-white">
                  <h4 className="text-sm font-bold text-slate-900 mb-2">{s.title}</h4>
                  <HelpBlockRenderer blocks={s.blocks} />
                </section>
              ))}
              {footerSection?.blocks?.find((b) => b.type === 'legal_footer')?.text && (
                <div className="border-t border-slate-200 pt-4 text-xs text-slate-600 flex gap-2">
                  <span className="text-slate-400">Footer:</span>
                  {footerSection.blocks.find((b) => b.type === 'legal_footer').text}
                </div>
              )}
            </>
          ) : (
            <>
              {sections.map((section, i) => (
                <SectionCard
                  key={`${section.section_key}-${section.id || i}`}
                  section={section}
                  sectionIndex={i}
                  onUpdate={updateSection}
                  onRemove={removeSection}
                  onUploadImage={uploadImage}
                  uploadBusy={uploadBusy}
                  canDelete={section.section_key !== 'legal-footer'}
                />
              ))}

              <button
                type="button"
                onClick={addSection}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-lg border-2 border-dashed border-slate-200 text-sm text-slate-500 hover:border-psg-teal hover:text-psg-teal transition-colors"
              >
                <PlusIcon className="w-4 h-4" />
                Add section
              </button>
            </>
          )}
        </div>
      </div>

      <p className="text-center text-xs text-slate-400 mt-4">
        Hover between blocks to <strong>+</strong> insert text, headings, bullets, callouts, or images
      </p>
    </div>
  )
}
